import { getAuthoringCollection } from '@platform/client';

import { publishReadinessKey } from '../authoring/readiness';

import type { GetAuthoringCollectionResponse } from '@platform/client';
import type { CollectionKind } from '@platform/schema/content';
import type { QueryClient } from '@tanstack/react-query';

/**
 * One collection, by its id, asked the same way from every detail screen.
 *
 * Its own route since VIS-18 gave the form a field the list does not carry —
 * `collection-row-serves-the-detail-screen.md`. The key is here rather than written out
 * per screen for the reason `readiness.ts` gives about its own — the title edit
 * invalidates it, and a key that drifted would leave one screen showing the old title
 * after a save with nothing failing anywhere.
 */
export const collectionQuery = (id: string) => ({
  queryKey: collectionKey(id),
  queryFn: () => getAuthoringCollection({ path: { id }, throwOnError: true }),
});

export const collectionKey = (id: string) => ['collection', id];

/**
 * Everything a write to the row itself makes stale — PATH-01.
 *
 * The same three as `membershipChanged` next door, with the row in place of its members.
 * The detail screen reads the row, the library list a page of rows and the publish panel
 * what its fields still fail. A write invalidating one and not the others leaves that
 * screen showing the old row with nothing failing anywhere. It was written out twice — once for the title edit, once for
 * archiving — before there was a third caller to disagree with them.
 */
export const collectionChanged = async (
  queries: QueryClient,
  id: string,
): Promise<void> => {
  await Promise.all([
    queries.invalidateQueries({ queryKey: collectionKey(id) }),
    queries.invalidateQueries({ queryKey: publishReadinessKey(id) }),
    queries.invalidateQueries({ queryKey: ['library'] }),
  ]);
};

/**
 * Where each kind's detail screen lives — GEN-60.
 *
 * Keyed by kind because `ToCollection` has a kind and needs a path. The three list
 * screens spell their own row links out, which is the one other place these strings
 * appear; a fourth spelling is what this exists to prevent.
 */
export const HOME: Record<CollectionKind, string> = {
  COURSE: '/library/courses',
  MODULE: '/library/modules',
  PATH: '/library/paths',
};

/** A collection as its detail screen reads it. */
export type Collection = GetAuthoringCollectionResponse;
